/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
    Checkbox,
    Divider,
    Grid,
    GridColumn,
    GridRow,
    Header,
    Icon,
    Image,
    Segment,
    Statistic,
} from 'semantic-ui-react'
import Zoom from 'react-medium-image-zoom'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import { MOBILE_THRESHOLD } from '../../../../constants'
import { formatToMaxLength } from '../../../../helpers/formatToMaxLength'
import useUser from '../../../Shared/UserProvider/useUser'
import { UltyMoney } from '@ulty/ulty-ui'
import Barcode from 'react-barcode'
import { useWebPSupportCheck } from 'react-use-webp-support-check'
import { FulfillmentState } from './OrderItemFulfillment/FulfillmentState'
import { FulfillmentIssueLabel } from './OrderItemFulfillment/FulfillmentIssueLabel'
import { FoundItemButton } from './OrderItemFulfillment/FoundItemButton'
import {
    OrderItemFulfillmentIssueBar,
} from './OrderItemFulfillment/OrderItemFulfillmentIssueBar/OrderItemFulfillmentIssueBar'
import { useOrderPickingScan } from '../OrderScanPicking'
import { useSaveFulfillment } from './OrderItemFulfillment/useSaveFulfillment'
import { OrderItemQuantity } from './OrderItemQuantity'
import { OrderItemPricingMetadata } from './OrderItemPricingMetadata'
import { validate } from 'barcoder'
import { NAVBAR_HEIGHT_IN_PX } from '../../../NavBar'

import { PricerBlinkButton } from '../../../../pricer/components/PricerBlinkButton/PricerBlinkButton'


const truncateString = (str, maxLength) => {
    return str?.length > maxLength ? formatToMaxLength(str, maxLength) : str
}

const OrderItemDescription = ({orderItem}) => {
    const maxLength = 50
    const description = truncateString(orderItem.object?.description || orderItem.description, maxLength)

    return (description && <div css={css`font-style: italic;
        font-size: 0.75em`}>{description}</div>)
}

const OrderItemDetail = React.memo(({orderItem}) => {
    const [t] = useTranslation()
    const {user} = useUser()

    const pricedByUnit = orderItem.item?.pricedByUnit
    const sellableUnit = orderItem?.sellableUnit
    const sellingOption = orderItem.item?.sellingOptions.find(so => so.measurementUnit.type === sellableUnit.type)

    const isPricedByWeightOrVolumeAndSoldByCount =
      pricedByUnit?.type !== 'COUNT' &&
      sellableUnit?.type === 'COUNT'

    return (
      <>
          <div css={css`
              display: flex;
              flex-direction: row;
              width: 100%;
              justify-content: space-around;
              height: 100%;
              max-height: 95px;
              padding-bottom: 10px;

              .statistic {
                  margin: 0;
              }

              .ui.statistic + .ui.statistic {
                  margin: 0;
              }
          `}>
              <Statistic size="mini">
                  <Statistic.Label css={css`font-size: .8em !important;`}>{t('order.quantity')}</Statistic.Label>
                  <Statistic.Value
                    css={css`
                        flex: 1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    `}
                    className={parseFloat(orderItem.quantity || 0) > 1 ? 'multi-items' : ''}>
                      <OrderItemQuantity
                        css={orderItem.quantity !== orderItem.initialQuantity ? css`
                            font-size: .6em;
                            font-weight: normal;
                            text-decoration: line-through;
                            color: black;
                            vertical-align: middle;
                        ` : undefined}
                        orderItem={orderItem}
                        quantity={orderItem.initialQuantity}
                      />

                      {orderItem.quantity !== orderItem.initialQuantity &&
                        <>
                            <Icon
                              style={{margin: '0 .5rem', verticalAlign: 'middle'}}
                              size="small"
                              color="black"
                              name="long arrow alternate right"
                            />

                            <OrderItemQuantity
                              css={css`
                                  vertical-align: middle;
                              `}
                              orderItem={orderItem}
                              quantity={orderItem.quantity}
                            />
                        </>
                      }
                  </Statistic.Value>

                  {isPricedByWeightOrVolumeAndSoldByCount && sellingOption && (
                    <span css={css`font-size: 0.7em; text-align: center;`}>
                        {(orderItem.totalPrice / orderItem.unitPrice).toFixed(3)}
                        {t(`order.detail.sellable_unit.${orderItem.item.pricedByUnit.type}.unit.${orderItem.item.pricedByUnit.unit}`)}
                      </span>
                  )}
              </Statistic>
              <Statistic size="mini">
                  <Statistic.Label
                    css={css`font-size: .8em !important;`}>
                      {t('order.order_item.total_price')}
                  </Statistic.Label>
                  <Statistic.Value css={css`font-size: 1rem !important`}>
                      {orderItem.totalPrice ?
                        <UltyMoney
                          readonly
                          amount={orderItem.totalPrice}
                          currencyCode={user.getCompanyCurrency()}
                          locale={user.getCompanyLocale()}
                        /> : '-'
                      }
                  </Statistic.Value>
              </Statistic>
          </div>
      </>
    )
})

const buildModifiers = (modifiers, prefix = 0) => {
    return modifiers.map((m) => {
        let Pre = () => null
        if (m.modifiers && m.modifiers.length > 0) {
            Pre = () => buildModifiers(m.modifiers, prefix + 1)
        }

        return (
          <React.Fragment key={m.id}>
              <div style={{paddingLeft: `${prefix * 10}px`}}>{`- ${m.name} (${m.quantity}x)`}</div>
              <Pre/>
          </React.Fragment>
        )
    })
}

const OrderItemRichBase = ({
                           order,
                           orderItem,
                           hideable,
                           isChecked: isCheckedProp,
                           expandState,
                           relatedReplacements,
                           onItemChecked,
                       }) => {
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`)
    const supportsWebP = useWebPSupportCheck()
    const itemRef = useRef(null)
    const contentRef = useRef(null)
    const [isChecked, setIsChecked] = useState(
        () => expandState === 'EXPAND' ? !(orderItem.quantity > 0) : isCheckedProp
    )
    const [contentHeight, setContentHeight] = useState(null)
    const {foundItemMutation} = useSaveFulfillment(order.id, orderItem.id)

    useEffect(() => {
        if (contentRef.current) {
            const updateHeight = () => {
                const height = contentRef.current.scrollHeight + NAVBAR_HEIGHT_IN_PX
                setContentHeight(height)
            }

            const timeoutId = setTimeout(updateHeight, 10)
            const resizeObserver = new ResizeObserver(() => {
                updateHeight()
            })

            resizeObserver.observe(contentRef.current)

            return () => {
                clearTimeout(timeoutId)
                resizeObserver.disconnect()
            }
        }
    }, [orderItem, relatedReplacements])

    const shouldBeExpandedWhenExpandStateChange = orderItem.quantity > 0

    const isCheckedPropRef = useRef(isCheckedProp)
    isCheckedPropRef.current = isCheckedProp

    useEffect(() => {
        setIsChecked(expandState === 'EXPAND' ? !shouldBeExpandedWhenExpandStateChange : isCheckedPropRef.current)
    }, [expandState, shouldBeExpandedWhenExpandStateChange]) // eslint-disable-line react-hooks/exhaustive-deps

    const prevIsCheckedPropRef = useRef(isCheckedProp)
    useEffect(() => {
        const prev = prevIsCheckedPropRef.current
        prevIsCheckedPropRef.current = isCheckedProp
        if (!prev && isCheckedProp) {
            setIsChecked(true)
            if (!isChecked) {
                onItemChecked?.(orderItem.id)
            }
        }
    }, [isChecked, isCheckedProp, onItemChecked, orderItem.id])

    const itemImage = useMemo(() => {
        if (!orderItem.image) return ''
        if (orderItem.image.indexOf('images.ulty.fr') === -1) {
            return orderItem.image
        }
        const path = orderItem.image.replace(/https?:\/\/images\.ulty\.fr\//, '')
        return supportsWebP
            ? `https://thumbnails.ulty.fr/250x250/webp/${path}`
            : `https://thumbnails.ulty.fr/250x250/png/${path}`
    }, [orderItem.image, supportsWebP])

    const modifiersTree = useMemo(
        () => orderItem.modifiers?.length > 0 ? buildModifiers(orderItem.modifiers) : null,
        [orderItem.modifiers]
    )

    const {scannedItemsMap} = useOrderPickingScan()
    const isItemPricedByCount = !orderItem.item || !orderItem.item.pricedByUnit || orderItem.item.pricedByUnit.type === 'COUNT'

    const shouldCollapse = isChecked && hideable

    return (
      <div id={`orderItem-${orderItem.id}`} ref={itemRef}>
          <div css={css`
              width: 100%;
          `}>
              <Header as="h5" attached="top" block css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  
                  &.ui.top.attached.header {
                      margin-left: unset;
                      margin-right: unset;
                  }

                  ${orderItem.quantity === 0 && 'text-decoration: line-through;'}
              `}>
                  <div>{`${orderItem.object?.name || orderItem.name}`}
                      <br/>
                      <div css={css` .ui.label:first-child {
                          margin-left: 0 !important
                      }`}>
                          <FulfillmentState
                            orderItem={orderItem}
                            scanValue={scannedItemsMap.get(orderItem.id)}
                          />

                          {orderItem.fulfillmentIssue &&
                            orderItem.fulfillmentIssue !== 'FOUND_ITEM' &&
                            orderItem.customerAckType !== 'AWAITING_ACTION' &&
                            <FulfillmentIssueLabel orderItem={orderItem}/>
                          }
                      </div>

                  </div>
                  {hideable &&
                    (orderItem.pickingMethod ||
                      orderItem.fulfillmentIssue ||
                      isItemPricedByCount) && <Checkbox
                      checked={isChecked}
                      onChange={() => {
                          const newIsChecked = !isChecked
                          setIsChecked(newIsChecked)
                          if (newIsChecked) {
                              onItemChecked?.(orderItem.id)
                          }
                          if (!isCheckedProp && ['RECEIVED', 'ACCEPTED', 'PREPARING'].includes(order.status)) {
                              foundItemMutation.mutate({orderItem, pickingMethod: 'MANUAL'})
                          }
                      }}
                    />}
              </Header>

              <div
                css={css`
                  max-height: ${shouldCollapse ? '0px' : contentHeight !== null ? `${contentHeight}px` : '9999px'};
                  overflow: hidden;
                  opacity: ${shouldCollapse ? '0' : '1'};
                  will-change: max-height, opacity;
                  transform: translateZ(0);
                  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
                  
                  border: 1px solid rgba(34, 36, 38, 0.15);
                  border-top: none;
                  border-bottom-left-radius: 0;
                  border-bottom-right-radius: 0;
                  
                  .ui.segment {
                    margin: 0;
                    border: none !important;
                    border-radius: 0 !important;
                    box-shadow: none !important;
                  }
                `}
              >
                <Segment
                  attached
                  key={orderItem.id}
                >
                  <div ref={contentRef}>
                      {orderItem.categoryName && (
                        <>
                            <Header as="h4">{orderItem.categoryName}</Header>
                            <Divider/>
                        </>
                      )}
                      <Grid
                        css={css`
                            &.ui.grid > .row {
                                padding-top: 0;
                            }
                        `}
                      >
                          <GridColumn tablet={8} css={css` ${isMobile ? 'padding-bottom: 0;' : ''}`}>
                          <Zoom>
                              <Image
                                src={itemImage}
                                css={css`
                                    max-height: 100px;
                                    margin: auto;
                                `}/>
                          </Zoom>

                        </GridColumn>
                        <GridColumn tablet={8} css={css`
                            display: flex !important;
                            flex-direction: column;

                            ${isMobile ? 'padding-bottom: 0;' : ''}
                            label {
                                font-weight: bold;
                            }
                        `}>
                          <OrderItemPricingMetadata css={css`margin-bottom: .3rem;`} orderItem={orderItem}/>
                          <OrderItemDescription orderItem={orderItem}/>

                          {validate(orderItem.object?.barcode) &&
                            <div css={css`
                                display: flex;
                                justify-content: flex-start;
                                max-height: 95px;
                            `}>
                                {orderItem.object?.barcode && (
                                  <Barcode
                                    value={orderItem.object.barcode}
                                    format="EAN13"
                                    displayValue={true}
                                    fontSize={15}
                                    height={60}
                                    width={1.5}
                                  />
                                )}
                            </div>
                          }
                        </GridColumn>

                        <>
                          {modifiersTree && (
                            <GridColumn width={16}>
                                {modifiersTree}
                            </GridColumn>
                          )}
                          <GridColumn width={16} css={css`
                              padding-top: 0 !important;
                              display: flex !important;
                              flex-direction: column !important;
                          `}>
                              <OrderItemDetail orderItem={orderItem}/>
                          </GridColumn>
                        </>

                        {hideable &&
                          order.isModifiable &&
                          (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                          (!orderItem.fulfillmentIssue ||
                            orderItem.fulfillmentIssue === 'PARTIAL_AVAILABILITY' ||
                            orderItem.fulfillmentIssue === 'SUBSTITUTION' ||
                            orderItem.fulfillmentIssue === 'FOUND_ITEM') &&
                          <GridRow stretched>
                              <GridColumn>
                                  <OrderItemFulfillmentIssueBar
                                    order={order}
                                    orderItem={orderItem}
                                    hasReplacement={relatedReplacements && relatedReplacements.length > 0}
                                  />
                              </GridColumn>
                          </GridRow>
                        }

                        {hideable &&
                          !orderItem.pickingMethod &&
                          (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                          (!orderItem.fulfillmentIssue || (['OUT_OF_ITEM', 'SUBSTITUTION'].includes(orderItem.fulfillmentIssue))) &&
                          ['RECEIVED', 'ACCEPTED', 'PREPARING'].includes(order.status) &&
                          <GridRow stretched>
                              <GridColumn>
                                  <FoundItemButton
                                    order={order}
                                    orderItem={orderItem}
                                  />
                              </GridColumn>
                          </GridRow>
                        }

                        {hideable &&
                          !orderItem.pickingMethod &&
                          (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                          (!orderItem.fulfillmentIssue || (['OUT_OF_ITEM', 'SUBSTITUTION'].includes(orderItem.fulfillmentIssue))) &&
                          ['PREPARING'].includes(order.status) &&
                          <GridRow stretched>
                              <GridColumn>
                                <PricerBlinkButton orderItem={orderItem} order={order} />
                              </GridColumn>
                          </GridRow>
                        }
                    </Grid>
                  </div>
                </Segment>
              </div>
          </div>

          {relatedReplacements &&  relatedReplacements.length > 0 && relatedReplacements.map((relatedReplacement) => (
            <>
                <div css={css`
                    display: flex;
                    justify-content: center;
                    margin-top: 0.4rem;
                    margin-bottom: 0.4rem;
                `}>
                    <Icon
                      name="arrow down"
                      size="large"
                    />
                </div>
                <OrderItemRich
                  orderItem={relatedReplacement}
                  hideable={hideable}
                  expandState={expandState}
                  isChecked={(!relatedReplacement.pickingMethod && !relatedReplacement.fulfillmentIssue) ? false : expandState !== 'EXPAND'}
                  relatedReplacements={undefined}
                  order={order}
                />
            </>
          ))}
      </div>
    )
}

const OrderItemRich = React.memo(OrderItemRichBase)

export { OrderItemRich }
